import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { CarFilters } from '@/components/cars/CarSearchFilters';
import type { CarCardData } from '@/components/cars/CarCard';
import type { Database, Tables } from '@/integrations/supabase/types';
import { tokenMatch } from '@/lib/search-match';
import { useModelsCatalog, type ModelWithBrand } from '@/hooks/useModelsCatalog';

type PowertrainType = Database['public']['Enums']['powertrain_type'];

export type SortOption = 'price_asc' | 'price_desc' | 'range_desc' | 'power_desc' | 'updated_desc' | 'votes_desc' | 'recommend_pct_desc' | 'year_desc' | 'year_asc' | 'charging_dc_desc' | 'trunk_desc' | 'isofix_desc';

const MIN_VOTES_FOR_PCT_SORT = 5;

interface UseCarSearchOptions {
  filters: CarFilters;
  sort: SortOption;
}

const QUERY_TIMEOUT_MS = 12000;

function withTimeout<T>(promise: Promise<T>, ms: number, errorMsg: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error(errorMsg)), ms)),
  ]);
}

export function useCarSearch({ filters, sort }: UseCarSearchOptions) {
  const catalog = useModelsCatalog();

  return useQuery({
    queryKey: ['car-search', filters, sort, catalog.data ? 'ready' : 'pending'],
    enabled: !!catalog.data,
    retry: 1,
    staleTime: 60 * 1000, // 1 min — search results cache briefly
    gcTime: 5 * 60 * 1000,
    queryFn: async () => {
      const cat = catalog.data!;

      // Step 1: narrow the model set client-side using cached catalog (no embedded joins needed).
      let modelPool: ModelWithBrand[] = cat.models;
      if (filters.brandId) modelPool = modelPool.filter((m) => m.brand_id === filters.brandId);
      if (filters.modelId) modelPool = modelPool.filter((m) => m.id === filters.modelId);
      if (filters.bodyType.length > 0) {
        const set = new Set(filters.bodyType);
        modelPool = modelPool.filter((m) => m.body_type && set.has(m.body_type));
      }
      if (filters.query) {
        modelPool = modelPool.filter((m) => tokenMatch([m.brand.name, m.name], filters.query!));
      }

      if (modelPool.length === 0) return [];
      const modelIds = modelPool.map((m) => m.id);

      // Step 2: variants — flat select, no embedded joins.
      let q = supabase
        .from('variants')
        .select('*')
        .eq('is_active', true)
        .in('model_id', modelIds);

      if (filters.powertrain.length > 0) {
        q = q.in('powertrain_type', filters.powertrain as PowertrainType[]);
      }
      if (filters.minTrunk) q = q.gte('trunk_l', filters.minTrunk);
      if (filters.minTowing) q = q.gte('tow_kg_braked', filters.minTowing);
      if (filters.minIsofix) q = q.gte('isofix_count', filters.minIsofix);
      if (filters.threeIsofixSecondRow === 'yes') q = q.eq('isofix_three_in_second_row', true);
      else if (filters.threeIsofixSecondRow === 'no') q = q.eq('isofix_three_in_second_row', false);

      const fetchVariants = async () => {
        const { data, error } = await q;
        if (error) throw new Error(`Database fejl: ${error.message}`);
        return (data || []) as Tables<'variants'>[];
      };

      const variants = await withTimeout(fetchVariants(), QUERY_TIMEOUT_MS, 'Timeout: Søgningen tog for lang tid');
      if (variants.length === 0) return [];

      // Variant-level token match (brand + model + variant)
      let activeVariants = variants;
      if (filters.query) {
        activeVariants = variants.filter((v) => {
          const m = cat.byId.get(v.model_id);
          if (!m) return false;
          return tokenMatch([m.brand.name, m.name, v.name], filters.query!);
        });
      }
      if (activeVariants.length === 0) return [];

      const variantIds = activeVariants.map((v) => v.id);

      // Step 3: specs + prices in parallel — chunked to stay under PostgREST URL limits.
      const CHUNK = 200;
      const chunks: string[][] = [];
      for (let i = 0; i < variantIds.length; i += CHUNK) chunks.push(variantIds.slice(i, i + CHUNK));

      const [specsRes, pricesRes] = await Promise.all([
        Promise.all(chunks.map((ids) =>
          supabase.from('powertrain_specs').select('*').in('variant_id', ids)
        )),
        Promise.all(chunks.map((ids) =>
          supabase.from('prices_dk').select('*, price_history(*)').in('variant_id', ids)
        )),
      ]);

      const specsByVariant = new Map<string, Tables<'powertrain_specs'>>();
      specsRes.forEach((r) => (r.data || []).forEach((s: any) => specsByVariant.set(s.variant_id, s)));

      const pricesByVariant = new Map<string, any[]>();
      pricesRes.forEach((r) =>
        (r.data || []).forEach((p: any) => {
          const arr = pricesByVariant.get(p.variant_id) || [];
          arr.push(p);
          pricesByVariant.set(p.variant_id, arr);
        })
      );

      const typePriority: Record<string, number> = { From: 0, List: 1, Campaign: 2, LeaseExample: 3 };

      let results: CarCardData[] = activeVariants
        .map((variant) => {
          const model = cat.byId.get(variant.model_id);
          if (!model) return null;
          const prices = (pricesByVariant.get(variant.id) || []).slice().sort((a, b) => {
            const diff = (typePriority[a.price_type] ?? 4) - (typePriority[b.price_type] ?? 4);
            return diff !== 0 ? diff : a.price_dkk - b.price_dkk;
          });
          return {
            variant,
            model,
            brand: model.brand,
            lowestPrice: prices[0] || null,
            specs: specsByVariant.get(variant.id) || null,
          } as CarCardData;
        })
        .filter((x): x is CarCardData => x !== null);

      // Post-filters that need specs/prices
      if (filters.maxPrice) results = results.filter(r => r.lowestPrice && r.lowestPrice.price_dkk <= filters.maxPrice!);
      if (filters.minRange) results = results.filter(r => r.specs?.range_wltp_km && r.specs.range_wltp_km >= filters.minRange!);
      if (filters.minSeats) {
        results = results.filter(r => {
          const seats = r.variant.seats || 0;
          const maxSeats = r.variant.max_seats || seats;
          // Når "medtag tilvalg" er slået fra, ignorér max_seats for varianter hvor ekstra sæder er et tilvalg
          const effectiveMax = (!filters.includeOptionalSeats && r.variant.extra_seats_optional) ? seats : maxSeats;
          return seats >= filters.minSeats! || effectiveMax >= filters.minSeats!;
        });
      }
      if (filters.minPower) results = results.filter(r => r.specs?.power_hp && r.specs.power_hp >= filters.minPower!);
      if (filters.drivetrain) results = results.filter(r => r.variant.drivetrain === filters.drivetrain);
      if (filters.minChargingDcKw) results = results.filter(r => r.specs?.charging_dc_kw && r.specs.charging_dc_kw >= filters.minChargingDcKw!);
      if (filters.maxAcceleration) results = results.filter(r => r.specs?.acceleration_0_100 && r.specs.acceleration_0_100 <= filters.maxAcceleration!);

      results = results.filter(r => r.variant.availability_status !== 'discontinued');
      if (filters.showUpcoming === 'only') results = results.filter(r => r.variant.availability_status === 'upcoming');
      else if (filters.showUpcoming === 'exclude') results = results.filter(r => r.variant.availability_status === 'on_sale');

      // Vote stats only when needed
      let voteMap = new Map<string, { likes: number; dislikes: number; total: number }>();
      if (sort === 'votes_desc' || sort === 'recommend_pct_desc') {
        const ids = results.map(r => r.variant.id);
        if (ids.length > 0) {
          const { data: stats } = await (supabase as any).rpc('get_variant_vote_stats', {
            _variant_ids: ids,
          });
          (stats as any[] || []).forEach(s => voteMap.set(s.variant_id, { likes: s.likes, dislikes: s.dislikes, total: s.total }));
        }
      }

      results.sort((a, b) => {
        switch (sort) {
          case 'price_asc':
            if (!a.lowestPrice) return 1;
            if (!b.lowestPrice) return -1;
            return a.lowestPrice.price_dkk - b.lowestPrice.price_dkk;
          case 'price_desc':
            if (!a.lowestPrice) return 1;
            if (!b.lowestPrice) return -1;
            return b.lowestPrice.price_dkk - a.lowestPrice.price_dkk;
          case 'range_desc':
            if (!a.specs?.range_wltp_km) return 1;
            if (!b.specs?.range_wltp_km) return -1;
            return b.specs.range_wltp_km - a.specs.range_wltp_km;
          case 'power_desc':
            if (!a.specs?.power_hp) return 1;
            if (!b.specs?.power_hp) return -1;
            return b.specs.power_hp - a.specs.power_hp;
          case 'votes_desc': {
            const av = voteMap.get(a.variant.id);
            const bv = voteMap.get(b.variant.id);
            return (bv?.likes || 0) - (av?.likes || 0);
          }
          case 'recommend_pct_desc': {
            const av = voteMap.get(a.variant.id);
            const bv = voteMap.get(b.variant.id);
            const aQual = (av?.total || 0) >= MIN_VOTES_FOR_PCT_SORT;
            const bQual = (bv?.total || 0) >= MIN_VOTES_FOR_PCT_SORT;
            if (aQual && !bQual) return -1;
            if (!aQual && bQual) return 1;
            const aPct = aQual ? (av!.likes / av!.total) : -1;
            const bPct = bQual ? (bv!.likes / bv!.total) : -1;
            return bPct - aPct;
          }
          case 'year_desc': {
            const ay = (a.variant as any).model_year ?? -Infinity;
            const by = (b.variant as any).model_year ?? -Infinity;
            return by - ay;
          }
          case 'year_asc': {
            const ay = (a.variant as any).model_year ?? Infinity;
            const by = (b.variant as any).model_year ?? Infinity;
            return ay - by;
          }
          case 'charging_dc_desc': {
            const av = a.specs?.charging_dc_kw;
            const bv = b.specs?.charging_dc_kw;
            if (av == null && bv == null) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            return bv - av;
          }
          case 'trunk_desc': {
            const av = a.variant.trunk_l;
            const bv = b.variant.trunk_l;
            if (av == null && bv == null) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            return bv - av;
          }
          case 'isofix_desc': {
            const av = (a.variant as any).isofix_count;
            const bv = (b.variant as any).isofix_count;
            if (av == null && bv == null) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            return bv - av;
          }
          case 'updated_desc':
          default:
            return new Date(b.variant.updated_at || 0).getTime() - new Date(a.variant.updated_at || 0).getTime();
        }
      });

      return results;
    },
  });
}
